import React from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import styles from './AdminLayout.module.css';
import { Header } from '../../components/layout/Header/Header';
import { User } from '../../types';
import { AdminProvider, useAdmin } from './AdminContext';
import { useAuth } from '../../contexts/AuthContext';

interface AdminLayoutProps {
  user: User;
  onLogout: () => void;
}

const AdminSidebar: React.FC = () => {
  const { stats } = useAdmin();
  const { isSuperAdmin } = useAuth();

  const navItems = [
    {
      to: '/admin',
      end: true,
      label: 'Dashboard',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      ),
    },
    ...(isSuperAdmin ? [{
      to: '/admin/companies',
      label: 'Klanten',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      badge: stats.totalCompanies,
    }] : []),
    {
      to: '/admin/projects',
      label: 'Projecten',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      ),
      badge: stats.totalProjects,
    },
    {
      to: '/admin/users',
      label: 'Gebruikers',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      badge: stats.totalUsers,
    },
    {
      to: '/admin/datasources',
      label: 'Databronnen',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        </svg>
      ),
    },
  ];

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <p className={styles.sidebarTitle}>Beheer</p>
      </div>
      <nav className={styles.sidebarNav}>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `${styles.navItem} ${isActive ? styles.active : ''}`
            }
          >
            <span className={styles.navIcon}>{item.icon}</span>
            <span>{item.label}</span>
            {item.badge !== undefined && item.badge > 0 && (
              <span className={styles.navBadge}>{item.badge}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className={styles.sidebarFooter}>
        OmgevingsPilot Admin
      </div>
    </aside>
  );
};

const AdminLayoutInner: React.FC<AdminLayoutProps> = ({ user, onLogout }) => {
  return (
    <div className={styles.adminLayout}>
      <Header
        user={user}
        onLogout={onLogout}
        showProjectSelector={false}
      />
      <div className={styles.adminBody}>
        <AdminSidebar />
        <div className={styles.mainContent}>
          <div className={styles.pageContent}>
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminLayout: React.FC<AdminLayoutProps> = (props) => {
  return (
    <AdminProvider>
      <AdminLayoutInner {...props} />
    </AdminProvider>
  );
};
